import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import VerifiedOrgMark from "./VerifiedOrgMark";
import { Alert, AlertDescription } from "@/components/ui/alert";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { API_BASE_URL } from "@/lib/apiBase";
import { cn } from "@/lib/utils";
import { useSocialPaths } from "@/lib/socialPaths";

const SocialExplorePage = () => {
  const { accessToken } = useAuth();
  const paths = useSocialPaths();
  const [communities, setCommunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [cRes, uRes, oRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/social/explore/communities`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get(`${API_BASE_URL}/social/explore/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get(`${API_BASE_URL}/social/orgs/explore`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);
      setCommunities(cRes.data?.communities || []);
      setUsers(uRes.data?.users || []);
      setOrgs(oRes.data?.orgs || []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load Explore"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl min-w-0 px-4 py-5 md:py-8 space-y-6 md:space-y-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Explore</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Discover communities, organizations, and people on Circa Social.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <section className="space-y-4">
        <h2 className="text-lg font-medium">Organizations</h2>
        {orgs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No organization profiles yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {orgs.map((o) => (
              <li key={o.username}>
                <Link
                  to={paths.org(o.username)}
                  className="flex min-w-0 items-center gap-3 rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors"
                >
                  <OrganizationAvatar
                    org={{
                      name: o.display_name,
                      avatar_url: o.avatar_url,
                    }}
                    className="h-10 w-10 shrink-0"
                    fallbackClassName="text-xs"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex min-w-0 items-center gap-1">
                      <span className="truncate font-medium" title={o.display_name}>
                        {o.display_name}
                      </span>
                      <VerifiedOrgMark org={o} className="shrink-0" />
                    </p>
                    <p className="truncate text-xs text-muted-foreground" title={`@${o.username}`}>
                      @{o.username}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="min-w-0 text-lg font-medium">Communities</h2>
          <Button type="button" size="sm" className="shrink-0" asChild>
            <Link to="/communities/new">Create community</Link>
          </Button>
        </div>
        {communities.length === 0 ? (
          <div className="space-y-3 rounded-lg border border-dashed border-border p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No communities yet. Start one for people to join and post.
            </p>
            <Button type="button" asChild>
              <Link to="/communities/new">Create community</Link>
            </Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {communities.map((c) => {
              const inviteOnly = c.join_policy === "invite_only";
              return (
                <li key={c.community_id}>
                  <Link
                    to={paths.community(c.community_id)}
                    className={cn(
                      "flex min-w-0 items-center gap-3 rounded-lg border border-border p-3 transition-colors",
                      inviteOnly
                        ? "opacity-80 hover:bg-muted/40"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <OrganizationAvatar
                      org={c.org}
                      name={c.org?.name || c.display_name}
                      className="h-10 w-10 shrink-0"
                      fallbackClassName="text-xs"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="min-w-0 truncate font-medium" title={c.display_name}>
                          {c.display_name}
                        </span>
                        <VerifiedOrgMark
                          org={c}
                          title="Verified community"
                          className="shrink-0"
                        />
                        {inviteOnly ? (
                          <Badge variant="secondary" className="shrink-0 text-xs">
                            Invite only
                          </Badge>
                        ) : null}
                      </div>
                      {c.description ? (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {c.description}
                        </p>
                      ) : null}
                    </div>
                    <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                      {c.member_count ?? 0} members
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-medium">People</h2>
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No other profiles to show yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {users.map((u) => (
              <li key={u.username}>
                <Link
                  to={paths.user(u.username)}
                  className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium" title={u.display_name}>
                      {u.display_name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground" title={`@${u.username}`}>
                      @{u.username}
                    </p>
                  </div>
                  {u.is_following ? (
                    <Badge variant="outline" className="text-xs">
                      Following
                    </Badge>
                  ) : (
                    <Button type="button" variant="secondary" size="sm" asChild>
                      <span>View</span>
                    </Button>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default SocialExplorePage;
